import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

/**
 * Two-context split:
 *   - SettersContext is stable (set once) so callers of useToolbar / usePageGuide
 *     don't re-render when the toolbar slot changes.
 *   - SlotsContext carries the actual rendered nodes; only TopBar consumes it.
 */

interface Setters {
  setTools: (tools: ReactNode) => void
  setGuide: (guide: ReactNode) => void
}

interface Slots {
  tools: ReactNode
  guide: ReactNode
}

const SettersContext = createContext<Setters | null>(null)
const SlotsContext = createContext<Slots>({ tools: null, guide: null })

export function ToolbarProvider({ children }: { children: ReactNode }) {
  const [tools, setTools] = useState<ReactNode>(null)
  const [guide, setGuide] = useState<ReactNode>(null)

  // Stable setters — these references never change for the lifetime of the provider.
  const setters = useRef<Setters>({ setTools, setGuide }).current

  const slots = useMemo<Slots>(() => ({ tools, guide }), [tools, guide])

  return (
    <SettersContext.Provider value={setters}>
      <SlotsContext.Provider value={slots}>{children}</SlotsContext.Provider>
    </SettersContext.Provider>
  )
}

export function useToolbarSlot(): ReactNode {
  return useContext(SlotsContext).tools
}

export function usePageGuideSlot(): ReactNode {
  return useContext(SlotsContext).guide
}

/**
 * Register toolbar content from inside a view. Auto-unregisters on unmount.
 *
 * @example
 *   useToolbar(
 *     <>
 *       <Button onClick={exportCsv}>CSV</Button>
 *       <Button onClick={rescore}>Rescore</Button>
 *     </>
 *   )
 *
 * Note: passing inline JSX is fine — the setter has stable identity, so this
 * does not trigger render loops even if the JSX is recreated each render.
 */
export function useToolbar(tools: ReactNode): void {
  const setters = useContext(SettersContext)
  useEffect(() => {
    setters?.setTools(tools)
    return () => setters?.setTools(null)
  }, [tools, setters])
}

/**
 * Register a guide pill (typically a <DocsLink />) next to the breadcrumb.
 * Auto-unregisters on unmount.
 */
export function usePageGuide(guide: ReactNode): void {
  const setters = useContext(SettersContext)
  useEffect(() => {
    setters?.setGuide(guide)
    return () => setters?.setGuide(null)
  }, [guide, setters])
}
